import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { entityKycStatusAction } from "../../actions/entityKycStatusAction";
import {
  useUploadDetails,
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  DOCS_BANK,
  renderFields,
  EntityDocumentsSection,
} from "./UploadDetailsData";
import GSTEntries from "./Components/GSTEntries";
import UtilityForm from "./Components/UtilityForm";
import MSMESection from "./Components/MSMESection";
import ThirdPartySection from "./Components/ThirdPartySection";
import DocumentUpload from "../../components/DocumentUpload";
import companyLogo from "../../assets/images/AcrossLogo.png";
import "./index.scss";

const UploadDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check if KYC already submitted — redirect to thank-you if so
  useEffect(() => {
    const id =
      searchParams.get("onboardingId") || searchParams.get("entityPrimaryId");
    if (!id) return;
    entityKycStatusAction(parseInt(id, 10)).then(({ isDocumentUploaded }) => {
      if (isDocumentUploaded) {
        const entityName = searchParams.get("EntityName") || "";
        const entityId =
          searchParams.get("entityId") || searchParams.get("EntityId") || "";
        navigate(
          `/thank-you?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}&alreadySubmitted=true`,
          { replace: true },
        );
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    formData,
    errors,
    setErrors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleDocumentUpload,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    handleEntityDocInputBlur,
    bankFiles,
    handleBankFileChange,
    showThirdParty,
    // beneficiaryMatchStatus, entityName — not used in JSX currently
    existingDocuments,
    // entityCategory, entityLOBType, contactPersonEmail, spocEmailAddress, // TODO: show in header
  } = useUploadDetails();

  // Helper: get existing uploaded file for a doc key
  const getExistingFile = (docKey) => {
    const doc = existingDocuments.find((d) => d.key === docKey);
    const file = doc?.files?.find((f) => f.isUploaded) || null;
    if (existingDocuments.length > 0) {
      console.log(
        `[getExistingFile] key="${docKey}" → found=${!!doc} → file=${file?.fileName || "none"}`,
      );
    }
    return file;
  };

  return (
    <div className="upload-details-page">
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header__brand">
          <img
            src={companyLogo}
            alt="Company logo"
            className="page-header__logo"
          />
        </div>
        <div className="page-header__text">
          <h1 className="page-title">Complete KYC OnBoarding Details</h1>
          <p className="page-subtitle">
            Fill in the remaining details to complete onboarding registration
          </p>
        </div>
      </div>

      {/* ── Single form card ── */}
      <form onSubmit={handleSubmit} className="details-form">
        <div className="form-card">
          {/* Entity Information */}
          <section className="form-section">
            <h2 className="section-title">KYC Information</h2>
            <div className="form-grid">
              {renderFields(
                ENTITY_INFO_FIELDS,
                formData,
                errors,
                handleChange,
                handleBlur,
              )}
            </div>
            <EntityDocumentsSection
              entityType={formData.entityType}
              entityFiles={entityFiles}
              onEntityFileChange={(key, file) => {
                handleEntityFileChange(key, file);
                handleDocumentUpload(key, file);
              }}
              entityDocInputs={entityDocInputs}
              onEntityDocInputChange={handleEntityDocInputChange}
              onEntityDocInputBlur={handleEntityDocInputBlur}
              errors={errors}
              existingDocuments={existingDocuments}
            />
          </section>

          <div className="section-divider" />

          {/* Contact Person — moved after address fields */}
          <section className="form-section">
            <h2 className="section-title">Contact Person Details</h2>
            <div className="form-grid">
              {renderFields(
                CONTACT_FIELDS,
                formData,
                errors,
                handleChange,
                handleBlur,
              )}
            </div>
          </section>

          <div className="section-divider" />

          {/* GST Information */}
          <section className="form-section">
            <h2 className="section-title">GST Information</h2>
            <div className="form-grid">
              {renderFields(
                GST_FIELDS,
                formData,
                errors,
                handleChange,
                handleBlur,
              )}
            </div>
            <GSTEntries
              isGSTRegistered={formData.isGSTRegistered}
              gstEntries={gstEntries}
              addGstEntry={addGstEntry}
              removeGstEntry={removeGstEntry}
              handleGstChange={handleGstChange}
              handleGstBlur={handleGstBlur}
              gstFiles={gstFiles}
              handleGstFileChange={(index, file) => {
                handleGstFileChange(index, file);
                handleDocumentUpload(`gst_file_${index}`, file);
                // clear cert error on upload
                if (file)
                  setErrors((prev) => {
                    const e = { ...prev };
                    delete e[`gst_certificate_${index}`];
                    return e;
                  });
              }}
              gstCertErrors={errors}
              gstErrors={gstErrors}
              existingGstFiles={existingDocuments
                .filter((d) => d.key.startsWith("gst_file_"))
                .sort((a, b) => {
                  const ai = parseInt(a.key.replace("gst_file_", ""), 10);
                  const bi = parseInt(b.key.replace("gst_file_", ""), 10);
                  return ai - bi;
                })
                .map((d) => d.files?.find((f) => f.isUploaded) || null)}
            />
            <UtilityForm
              isGSTRegistered={formData.isGSTRegistered}
              formData={formData}
              errors={errors}
              handleChange={handleChange}
              handleBlur={handleBlur}
              utilityFile={utilityFile}
              handleUtilityFileChange={(file) => {
                setUtilityFile(file);
                handleDocumentUpload("utility_file", file);
              }}
              existingUtilityFile={getExistingFile("doc_utility_bill")}
            />
          </section>

          <div className="section-divider" />

          {/* MSME Information */}
          <section className="form-section">
            <h2 className="section-title">MSME Information</h2>
            <div className="form-grid">
              {renderFields(
                MSME_FIELDS,
                formData,
                errors,
                handleChange,
                handleBlur,
              )}
            </div>
            <MSMESection
              isMSMERegistered={formData.isMSMERegistered}
              formData={formData}
              errors={errors}
              handleChange={handleChange}
              handleBlur={handleBlur}
              msmeFile={msmeFile}
              handleMsmeFileChange={(file) => {
                setMsmeFile(file);
                handleDocumentUpload("msme_file", file);
              }}
              existingMsmeFile={getExistingFile("doc_msme_certificate")}
            />
          </section>

          <div className="section-divider" />

          {/* Bank Details */}
          <section className="form-section">
            <h2 className="section-title">Bank Details</h2>
            {/* {beneficiaryMatchStatus && (
              <div
                className={`beneficiary-status beneficiary-status--${beneficiaryMatchStatus}`}
              >
                {beneficiaryMatchStatus === "match"
                  ? "✓ Trade name and beneficiary name match."
                  : "✗ Trade name and beneficiary name do not match — third party payment details required."}
              </div>
            )} */}
            <div className="form-grid-radio">
              <label className="radio-label">
                Provide Bank Details?<span className="required-mark"> *</span>
              </label>
              <div className="radio-group">
                {["yes", "no"].map((v) => (
                  <label key={v} className="radio-option">
                    <input
                      type="radio"
                      name="hasBankDetails"
                      value={v}
                      checked={formData.hasBankDetails === v}
                      onChange={handleChange}
                    />
                    {v === "yes" ? "Yes" : "No"}
                  </label>
                ))}
              </div>
              {errors.hasBankDetails && (
                <span className="input-error-msg">{errors.hasBankDetails}</span>
              )}
            </div>
            {formData.hasBankDetails === "yes" && (
              <>
                <div className="form-grid">
                  {renderFields(
                    BANK_FIELDS,
                    formData,
                    errors,
                    handleChange,
                    handleBlur,
                  )}
                </div>
                {/* Bank Documents */}
                <div className="entity-docs-section">
                  <p className="entity-docs-note">Required Bank Documents</p>
                  <div className="entity-docs-grid">
                    {DOCS_BANK.map((doc) => (
                      <DocumentUpload
                        key={doc.key}
                        label={doc.label}
                        name={doc.key}
                        file={bankFiles[doc.key] || null}
                        existingFile={getExistingFile(doc.key)}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          handleBankFileChange(doc.key, file);
                          handleDocumentUpload(doc.key, file);
                          // clear upload error on file select
                          if (file)
                            setErrors((prev) => {
                              const e = { ...prev };
                              delete e[doc.key];
                              return e;
                            });
                        }}
                        required={doc.required}
                        error={errors[doc.key] || ""}
                      />
                    ))}
                  </div>
                </div>
                <ThirdPartySection
                  show={showThirdParty}
                  formData={formData}
                  errors={errors}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  thirdPartyFile={thirdPartyFile}
                  handleThirdPartyFileChange={(file) => {
                    setThirdPartyFile(file);
                    handleDocumentUpload("third_party_file", file);
                  }}
                />
              </>
            )}
          </section>

          {/* ── Submit footer inside the card ── */}
          <div className="form-footer">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UploadDetails;
